import { useEffect, useRef, useState, type FormEvent } from "react";
import axios from "axios";
import { useQueryClient } from "react-query";
import { heritageApiErrorMessage } from "./heritageApiErrorMessage";
import {
  formatMaskedApiKeyHint,
  useHeritageStoredCredentials,
} from "../hooks/useHeritageStoredCredentials";
import { useAuthContext } from "contexts/auth";
import { invalidateHeritageCatalogDownloadStatus } from "../hooks/useHeritageCatalogDownloadStatus";

type Props = {
  credentialsExist: boolean;
  /**
   * Credentials have been accepted by Heritage, not merely stored. Saving is not
   * proof they work, so the confirmed styling waits for this rather than for
   * credentialsExist.
   */
  isValidated?: boolean;
  autoOpenCredentials?: boolean;
  onAutoOpenCredentialsHandled?: () => void;
  catalogSyncInProgress?: boolean;
};

const HeritageCredentialsSection = ({
  credentialsExist,
  isValidated = false,
  autoOpenCredentials = false,
  onAutoOpenCredentialsHandled,
  catalogSyncInProgress = false,
}: Props) => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const queryClient = useQueryClient();
  const auth = useAuthContext();
  const companyId = auth?.company_id ?? null;
  const [isEditing, setIsEditing] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [customerAccountId, setCustomerAccountId] = useState("");
  const [customerApiKey, setCustomerApiKey] = useState("");
  const [feedback, setFeedback] = useState<string | null>(null);
  const [feedbackIsError, setFeedbackIsError] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);
  const [confirmingRemove, setConfirmingRemove] = useState(false);
  const [savedLocally, setSavedLocally] = useState(false);

  const showSavedState = Boolean(credentialsExist) || savedLocally;

  // Three states, because "entered" and "working" are different things and the
  // panel previously showed both as confirmed:
  //   nothing entered      -> the entry form
  //   entered, unconfirmed -> neutral, and Heritage has not accepted these yet
  //   confirmed            -> the settled styling
  const tone = isValidated
    ? {
        container: "bg-green-50 border-green-200",
        header: "hover:bg-green-100/80",
        chevron: "text-green-800",
        title: "text-green-800",
        body: "border-green-200 bg-green-50/50",
        label: "Heritage credentials saved",
        note: null as string | null,
      }
    : {
        container: "bg-gray-50 border-gray-200",
        header: "hover:bg-gray-100/80",
        chevron: "text-gray-700",
        title: "text-gray-800",
        body: "border-gray-200 bg-gray-50/50",
        label: "Heritage credentials saved — not connected yet",
        note: "Connect this vendor to Heritage to confirm these credentials.",
      };
  const storedCredentialsQuery = useHeritageStoredCredentials(showSavedState);

  useEffect(() => {
    if (!autoOpenCredentials) return;

    if (!showSavedState) {
      setIsEditing(true);
      setFeedback(null);
      setFeedbackIsError(false);
    } else {
      setIsExpanded(true);
    }

    const scrollTimer = window.setTimeout(() => {
      sectionRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }, 100);

    onAutoOpenCredentialsHandled?.();

    return () => window.clearTimeout(scrollTimer);
  }, [autoOpenCredentials, showSavedState, onAutoOpenCredentialsHandled]);

  useEffect(() => {
    if (catalogSyncInProgress) {
      setConfirmingRemove(false);
    }
  }, [catalogSyncInProgress]);

  const storedAccountId = storedCredentialsQuery.data?.customerAccountId ?? "";
  const storedApiKeyHint = formatMaskedApiKeyHint(storedCredentialsQuery.data?.apiKeyLast4);

  const invalidateCredentialQueries = async () => {
    await queryClient.invalidateQueries("heritage-connection");
    await queryClient.invalidateQueries(["heritage-auth-status"]);
    await queryClient.invalidateQueries(["heritage-stored-credentials"]);
  };

  const handleSave = async (e?: FormEvent | React.MouseEvent) => {
    e?.preventDefault();
    e?.stopPropagation();
    setFeedback(null);
    setFeedbackIsError(false);

    const accountTrimmed = customerAccountId.trim();
    const keyTrimmed = customerApiKey.trim();

    if (!showSavedState) {
      if (!accountTrimmed || !keyTrimmed) {
        setFeedbackIsError(true);
        setFeedback("Customer Account ID and API Key are both required.");
        return;
      }
    } else if (!accountTrimmed && !keyTrimmed) {
      setFeedbackIsError(true);
      setFeedback("Provide a Customer Account ID and/or API Key to update.");
      return;
    }

    setIsSaving(true);
    try {
      const payload: Record<string, string> = {};
      if (accountTrimmed) payload.customer_account_id = accountTrimmed;
      if (keyTrimmed) payload.customer_api_key = keyTrimmed;

      await axios.post(
        `${process.env.REACT_APP_SERVER_URL}/api/v1/company/heritage/update-credentials`,
        payload
      );
      await invalidateCredentialQueries();

      try {
        await axios.post(`${process.env.REACT_APP_SERVER_URL}/api/v1/company/heritage/connect`);
        await invalidateCredentialQueries();
      } catch (connectError) {
        console.error(
          "[Heritage] Connect after credential save failed:",
          heritageApiErrorMessage(connectError) ?? "Connect failed."
        );
      }

      setCustomerAccountId("");
      setCustomerApiKey("");
      setIsEditing(false);
      setIsExpanded(true);
      setSavedLocally(true);
      setFeedbackIsError(false);
      setFeedback("Heritage credentials saved.");
    } catch (error) {
      setFeedbackIsError(true);
      setFeedback(heritageApiErrorMessage(error) ?? "Failed to save Heritage credentials.");
      console.error(
        "[Heritage] Save credentials failed:",
        heritageApiErrorMessage(error) ?? "Unknown error"
      );
    } finally {
      setIsSaving(false);
    }
  };

  const startEditing = () => {
    setConfirmingRemove(false);
    setIsEditing(true);
    setCustomerAccountId(storedAccountId);
    setCustomerApiKey("");
    setFeedback(null);
    setFeedbackIsError(false);
  };

  const cancelEditing = () => {
    setConfirmingRemove(false);
    setIsEditing(false);
    setCustomerAccountId("");
    setCustomerApiKey("");
    setFeedback(null);
    setFeedbackIsError(false);
    setIsExpanded(false);
  };

  const handleRemoveCredentials = async () => {
    setFeedback(null);
    setFeedbackIsError(false);
    setIsRemoving(true);
    try {
      await axios.delete(
        `${process.env.REACT_APP_SERVER_URL}/api/v1/company/heritage/remove-credentials`
      );
      setConfirmingRemove(false);
      setSavedLocally(false);
      setIsEditing(false);
      setCustomerAccountId("");
      setCustomerApiKey("");
      await invalidateCredentialQueries();
      await queryClient.invalidateQueries(["heritage-branches"]);
      await invalidateHeritageCatalogDownloadStatus(queryClient, companyId);
      setFeedbackIsError(false);
      setFeedback("Heritage credentials removed.");
    } catch (error) {
      setFeedbackIsError(true);
      setFeedback(heritageApiErrorMessage(error) ?? "Failed to remove Heritage credentials.");
      console.error(
        "[Heritage] Remove credentials failed:",
        heritageApiErrorMessage(error) ?? "Unknown error"
      );
    } finally {
      setIsRemoving(false);
    }
  };

  const catalogSyncBlockedTitle =
    "Cannot change Heritage credentials while a catalog download is in progress.";

  const removeCredentialsControl = confirmingRemove ? (
    <div
      className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:gap-x-3"
      role="group"
      aria-label="Confirm remove Heritage credentials"
    >
      <p className="text-xs text-gray-700 m-0 sm:flex-1 sm:min-w-[10rem]">
        Remove credentials and disconnect Heritage for this company? Branch catalog rows are kept.
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            void handleRemoveCredentials();
          }}
          disabled={isRemoving || isSaving || catalogSyncInProgress}
          title={catalogSyncInProgress ? catalogSyncBlockedTitle : undefined}
          className="inline-flex min-h-[44px] items-center justify-center rounded-md bg-red-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-red-700 disabled:opacity-50 touch-manipulation"
        >
          {isRemoving ? "Removing…" : "Confirm remove"}
        </button>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setConfirmingRemove(false);
          }}
          disabled={isRemoving}
          className="inline-flex min-h-[44px] items-center justify-center px-2 text-xs font-medium text-gray-700 hover:text-gray-900 hover:underline disabled:opacity-50 touch-manipulation"
        >
          Cancel
        </button>
      </div>
    </div>
  ) : (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        if (catalogSyncInProgress) return;
        setConfirmingRemove(true);
      }}
      disabled={isRemoving || isSaving || catalogSyncInProgress}
      title={catalogSyncInProgress ? catalogSyncBlockedTitle : undefined}
      className="text-xs font-medium text-red-600 hover:text-red-700 hover:underline disabled:opacity-50 min-h-[44px] px-1 touch-manipulation"
    >
      Remove credentials
    </button>
  );

  const canSubmit =
    !showSavedState
      ? Boolean(customerAccountId.trim() && customerApiKey.trim())
      : Boolean(customerAccountId.trim() || customerApiKey.trim());

  if (showSavedState && !isEditing) {
    return (
      <div
        ref={sectionRef}
        id="heritage-credentials-section"
        className={`text-sm text-gray-700 border rounded-md overflow-hidden ${tone.container}`}
      >
        <div className="flex items-stretch min-h-[44px]">
          <button
            type="button"
            onClick={() => setIsExpanded((prev) => !prev)}
            aria-expanded={isExpanded}
            className={`flex flex-1 items-center gap-2 px-3 py-2 text-left transition touch-manipulation ${tone.header}`}
          >
            <span
              className={`inline-flex shrink-0 transition-transform ${tone.chevron} ${
                isExpanded ? "rotate-90" : ""
              }`}
              aria-hidden
            >
              ▶
            </span>
            <span className={`font-semibold ${tone.title}`}>{tone.label}</span>
          </button>
          <button
            type="button"
            onClick={startEditing}
            disabled={catalogSyncInProgress}
            title={catalogSyncInProgress ? catalogSyncBlockedTitle : undefined}
            className="shrink-0 self-center text-sm font-medium text-primary hover:underline min-h-[44px] px-3 touch-manipulation disabled:opacity-50 disabled:cursor-not-allowed disabled:no-underline"
          >
            Update credentials
          </button>
        </div>

        {isExpanded && (
          <div className={`border-t px-3 py-3 space-y-3 ${tone.body}`}>
            {tone.note && <p className="text-xs text-gray-600 m-0">{tone.note}</p>}
            {storedCredentialsQuery.isLoading ? (
              <p className="text-xs text-gray-600 m-0">Loading saved credentials…</p>
            ) : storedCredentialsQuery.isError ? (
              <p className="text-xs text-red-600 m-0">Unable to load saved credentials.</p>
            ) : (
              <div className="space-y-3">
                <div>
                  <p className="text-xs font-medium text-gray-600 m-0 mb-1">Customer Account ID</p>
                  <p className="font-mono text-sm text-gray-900 m-0 break-all">
                    {storedAccountId || "—"}
                  </p>
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-600 m-0 mb-1">Customer API Key</p>
                  <p className="font-mono text-sm text-gray-900 m-0 break-all">{storedApiKeyHint}</p>
                </div>
              </div>
            )}

            {feedback && (
              <p
                className={`text-xs m-0 ${feedbackIsError ? "text-red-600" : "text-green-700"}`}
                role={feedbackIsError ? "alert" : "status"}
              >
                {feedback}
              </p>
            )}

            <div className="pt-1 border-t border-green-200/80">{removeCredentialsControl}</div>
          </div>
        )}
      </div>
    );
  }

  if (!showSavedState && !isEditing) {
    return (
      <div
        ref={sectionRef}
        id="heritage-credentials-section"
        className="text-sm text-gray-700 bg-amber-50 border border-amber-200 rounded-md overflow-hidden"
      >
        <div className="flex items-stretch min-h-[44px]">
          <div className="flex flex-1 items-center gap-2 px-3 py-2">
            <span className="font-semibold text-amber-900">Heritage credentials required</span>
          </div>
          <button
            type="button"
            onClick={() => {
              setIsEditing(true);
              setFeedback(null);
              setFeedbackIsError(false);
            }}
            className="shrink-0 self-center text-sm font-medium text-primary hover:underline min-h-[44px] px-3 touch-manipulation"
          >
            Add credentials
          </button>
        </div>

        {feedback ? (
          <div className="border-t border-amber-200 px-3 py-3 bg-amber-50/50">
            <p
              className={`text-xs m-0 ${feedbackIsError ? "text-red-600" : "text-green-700"}`}
              role={feedbackIsError ? "alert" : "status"}
            >
              {feedback}
            </p>
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <div
      ref={sectionRef}
      id="heritage-credentials-section"
      className={`text-sm text-gray-700 rounded-md p-3 space-y-3 border ${
        showSavedState ? "bg-white border-gray-200" : "bg-amber-50 border-amber-200"
      }`}
      onClick={(e) => e.stopPropagation()}
    >
      <div>
        <p className="font-semibold text-gray-900 m-0">
          {showSavedState ? "Update Heritage credentials" : "Heritage credentials required"}
        </p>
        <p className="text-xs text-gray-600 mt-1 m-0">
          Enter your Heritage Customer Account ID and API Key to connect this vendor to Heritage.
        </p>
      </div>

      <div className="space-y-2">
        <label htmlFor="heritage-customer-account-id" className="block text-xs font-medium text-gray-700">
          Customer Account ID
        </label>
        <input
          id="heritage-customer-account-id"
          type="text"
          autoComplete="off"
          className="w-full min-h-[44px] px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
          placeholder="e.g. SUPLHLSG"
          value={customerAccountId}
          onChange={(e) => setCustomerAccountId(e.target.value)}
        />
      </div>

      <div className="space-y-2">
        <label htmlFor="heritage-customer-api-key" className="block text-xs font-medium text-gray-700">
          Customer API Key
        </label>
        <input
          id="heritage-customer-api-key"
          type="password"
          autoComplete="new-password"
          className="w-full min-h-[44px] px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
          placeholder={
            showSavedState
              ? `Leave blank to keep current key (${storedApiKeyHint})`
              : "Enter API key"
          }
          value={customerApiKey}
          onChange={(e) => setCustomerApiKey(e.target.value)}
        />
      </div>

      {feedback && (
        <p
          className={`text-xs m-0 ${feedbackIsError ? "text-red-600" : "text-green-700"}`}
          role={feedbackIsError ? "alert" : "status"}
        >
          {feedback}
        </p>
      )}

      <div className="flex flex-wrap items-center justify-between gap-3 pt-1">
        <button
          type="button"
          onClick={handleSave}
          disabled={isSaving || isRemoving || !canSubmit}
          className="inline-flex min-h-[44px] w-full sm:w-auto items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-semibold text-white shadow-sm hover:brightness-[0.9] disabled:opacity-50 touch-manipulation"
        >
          {isSaving ? "Saving..." : "Save credentials"}
        </button>
        <button
          type="button"
          onClick={cancelEditing}
          disabled={isRemoving}
          className="inline-flex min-h-[44px] w-full sm:w-auto items-center justify-center rounded-md bg-white px-4 py-2 text-sm font-semibold text-gray-800 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 touch-manipulation sm:ml-auto"
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

export default HeritageCredentialsSection;
